import { computed, ref } from "vue";
import type { PurchaseOrder } from "../../../redistribution/models/PurchaseOrder.js";

export interface SelectionRowLike {
  poNumber: string;
  estArrival: string;
  totalItems: number;
  po: PurchaseOrder;
}

interface GridNodeLike {
  data: unknown;
  setSelected: (selected: boolean) => void;
}

interface GridApiLike {
  getSelectedRows: () => unknown[];
  forEachNode: (callback: (node: GridNodeLike) => void) => void;
}

interface GridSelectionOptions {
  getGridApi: () => GridApiLike | undefined;
  getStoredPoNumbers: () => string[];
  setStoredPoNumbers: (poNumbers: string[]) => void;
  setStoredPurchaseOrders: (purchaseOrders: PurchaseOrder[]) => void;
  toSelectionRow: (row: unknown) => SelectionRowLike | undefined;
  isDetailRow: (row: unknown) => boolean;
  getRowItemCount?: (row: SelectionRowLike) => number;
}

export function useGridSelection(options: GridSelectionOptions) {
  const selectedRows = ref<SelectionRowLike[]>([]);

  const totalSelectedItems = computed(() => {
    let sum = 0;
    for (const row of selectedRows.value) {
      const count = options.getRowItemCount
        ? options.getRowItemCount(row)
        : row.totalItems;
      sum += Number.isFinite(count) ? count : 0;
    }
    return sum;
  });

  function getSelectedPurchaseOrders(): PurchaseOrder[] {
    const purchaseOrders: PurchaseOrder[] = [];
    for (const row of selectedRows.value) {
      purchaseOrders.push(row.po);
    }

    return purchaseOrders.toSorted((left, right) =>
      left.arrivalDate.localeCompare(right.arrivalDate)
    );
  }

  function persistSelection(rows: SelectionRowLike[]): void {
    const uniqueByPoNumber = new Map<string, SelectionRowLike>();
    for (const row of rows) {
      uniqueByPoNumber.set(row.poNumber, row);
    }

    selectedRows.value = [...uniqueByPoNumber.values()];
    options.setStoredPoNumbers(selectedRows.value.map((row) => row.poNumber));
    options.setStoredPurchaseOrders(getSelectedPurchaseOrders());
  }

  function onSelectionChanged(): void {
    const api = options.getGridApi();
    if (!api) {
      return;
    }

    const selectedGridRows: SelectionRowLike[] = [];
    for (const row of api.getSelectedRows()) {
      if (options.isDetailRow(row)) {
        continue;
      }

      const selectionRow = options.toSelectionRow(row);
      if (selectionRow) {
        selectedGridRows.push(selectionRow);
      }
    }

    persistSelection(selectedGridRows);
  }

  function restoreSelections(): void {
    const api = options.getGridApi();
    if (!api) {
      return;
    }

    const poNumbersToRestore = new Set(options.getStoredPoNumbers());
    if (poNumbersToRestore.size === 0) {
      return;
    }

    const restoredRows = new Map<string, SelectionRowLike>();

    api.forEachNode((node) => {
      if (options.isDetailRow(node.data)) {
        return;
      }

      const selectionRow = options.toSelectionRow(node.data);
      if (!selectionRow) {
        return;
      }

      if (poNumbersToRestore.has(selectionRow.poNumber)) {
        restoredRows.set(selectionRow.poNumber, selectionRow);
        node.setSelected(true);
      }
    });

    persistSelection([...restoredRows.values()]);
  }

  function removeFromSelection(poNumber: string): void {
    const api = options.getGridApi();
    if (api) {
      api.forEachNode((node) => {
        if (options.isDetailRow(node.data)) {
          return;
        }

        const selectionRow = options.toSelectionRow(node.data);
        if (!selectionRow) {
          return;
        }

        if (selectionRow.poNumber === poNumber) {
          node.setSelected(false);
        }
      });
    }

    const remainingRows: SelectionRowLike[] = [];
    for (const row of selectedRows.value) {
      if (row.poNumber !== poNumber) {
        remainingRows.push(row);
      }
    }
    persistSelection(remainingRows);
  }

  return {
    selectedRows,
    getSelectedPurchaseOrders,
    totalSelectedItems,
    onSelectionChanged,
    restoreSelections,
    removeFromSelection,
  };
}
